import React, { useEffect, useState, useMemo } from 'react';
import { Button, Divider, Input, notification, Select } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { t } from '../../../i18n/i18n';
import DeleteForever from '../../../assets/screening/DeleteForever';
import EditPencilIcon from '../../../assets/common/EditPencilIcon';
import InfoTooltip from '../../InfoTooltip/InfoTooltip';
import { ConfirmModal } from '../../Categories/modal/ConfirmModal';
import { AnswerTypeEnum } from '../../../constants/enums';

const { Option } = Select;

export default function AdditionalQuestionsCatalog({
  additionalQuestions,
  onAddQuestion,
  onDeleteQuestion,
  onShowAddQuestion,
  customTestGroup,
}) {
  const [questionToDelete, setQuestionToDelete] = useState();
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState('ALL');
  const [open, setOpen] = useState(false);
  const [originalAvailableTypes, setOriginalAvailableTypes] = useState(new Set());

  // Filtrado local (client-side) de preguntas
  const filteredQuestions = useMemo(() => {
    let filtered = additionalQuestions ?? [];

    // Filtrar por tipo
    if (filterType !== 'ALL') {
      filtered = filtered.filter((q) => q.answerType === filterType);
    }

    // Filtrar por texto de búsqueda
    if (searchText.trim() !== '') {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((q) => {
        const questionText = q.text?.toLowerCase() || '';
        const answerTypeLabel = t(`CREATE_SCREENING_YOUR_QUESTIONS_${q.answerType}`).toLowerCase();
        return questionText.includes(searchLower) || answerTypeLabel.includes(searchLower);
      });
    }

    return filtered;
  }, [additionalQuestions, searchText, filterType]);

  const handleSelectQuestion = (questionId) => {
    const test = filteredQuestions.find((x) => x.testId === questionId);

    // Validar contra customTestGroup (estado Redux)
    const alreadyAdded = customTestGroup?.some((q) => q.testId === questionId || q.id === questionId);

    if (alreadyAdded) {
      notification.open({
        message: t('CREATE_ADDITIONAL_QUESTIONS_ALREADY_ADDED'),
        type: 'warning',
      });
    } else {
      // Usar el handler unificado directamente
      onAddQuestion([test]);
    }
  };

  const handleDeleteQuestion = async (question) => {
    setOpen(false);
    await onDeleteQuestion(question?.testId);
    setQuestionToDelete(undefined);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
  };

  const handleFilterTypeChange = (value) => {
    setFilterType(value);
  };


  // Guardar los tipos originales cuando no hay filtros aplicados
  useEffect(() => {
    if (additionalQuestions && additionalQuestions.length > 0 && filterType === 'ALL' && !searchText) {
      const types = new Set(additionalQuestions.map((q) => q.answerType).filter(Boolean));
      setOriginalAvailableTypes(types);
    }
  }, [additionalQuestions, filterType, searchText]);

  // Forzar re-renderizado del dropdown cuando cambia el tamaño de ventana/zoom
  useEffect(() => {
    const handleResize = () => {
      // Cerrar dropdown temporalmente si está abierto para forzar recálculo de ancho
      if (open) {
        setOpen(false);
        setTimeout(() => setOpen(true), 0);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [open]);

  // Generar opciones dinámicamente desde AnswerTypeEnum
  const questionTypeOptions = useMemo(() => {
    const availableOptions = Object.values(AnswerTypeEnum)
      .filter((type) => originalAvailableTypes.has(type))
      .map((type) => ({
        value: type,
        label: t(`CREATE_SCREENING_YOUR_QUESTIONS_${type}`),
      }));

    if (availableOptions.length > 0) {
      return [{ value: 'ALL', label: t('ALL_TYPES') }, ...availableOptions];
    }
    return availableOptions;
  }, [originalAvailableTypes]);

  return (
    <div className="px-2 rounded-lg mb-3">
      <ConfirmModal
        showModal={questionToDelete !== undefined}
        setShowModal={() => setQuestionToDelete(undefined)}
        submitAction={() => {
          handleDeleteQuestion(questionToDelete);
        }}
        title={t('QUESTION_DELETE_ADDITIONAL_TEST')}
        description={t('QUESTION_DELETE_ADDITIONAL_TEST_DETAIL')}
      />

      <div className="my-1 my-sm-0 ml-0 d-flex justify-content-end align-items-center">
        <span
          className="text-light-black font-weight-400 text-base my-2 px-3"
          data-testid="create-evaluation-additional-questions-subtitle-span"
        >
          {t('CREATE_ADDITIONAL_QUESTIONS_SUBTITLE')}
        </span>
        <InfoTooltip
          placement="top"
          content={
            <div className="d-flex flex-column p-2 ml-2">
              <span className="text-xs mb-2">{t('CREATE_ADDITIONAL_QUESTIONS_INFO')}</span>
            </div>
          }
        />
      </div>

      <div className="px-3 mr-3 ml-5">
        <Select
          placeholder={t('CREATE_ADDITIONAL_QUESTIONS_SELECT_PLACEHOLDER')}
          value={null}
          style={{
            width: '85%',
            marginTop: '1rem',
            marginBottom: '1rem',
          }}
          variant="filled"
          open={open}
          onDropdownVisibleChange={(visible) => setOpen(visible)}
          getPopupContainer={(trigger) => trigger.parentNode}
          dropdownRender={(menu) => (
            <div style={{ minHeight: '400px' }}>
              <div className="p-2">
                <Input
                  placeholder={t('CREATE_ADDITIONAL_QUESTIONS_SEARCH_PLACEHOLDER')}
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={handleSearchChange}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="p-2">
                <Select
                  style={{ width: '100%' }}
                  placeholder={t('FILTER_BY_TYPE') || 'Filtrar por tipo'}
                  value={filterType}
                  onChange={handleFilterTypeChange}
                  onClick={(e) => e.stopPropagation()}
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {questionTypeOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </div>
              <div className="d-flex align-items-center justify-content-between p-1">
                <span className="ml-2 text-dark font-weight-600 w-75">
                  {t('CREATE_ADDITIONAL_QUESTIONS_ADD')}
                </span>
                <Button
                  type="link"
                  icon={
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip className="" placement="top">
                          {t('CREATE_ADDITIONAL_QUESTIONS_ADD')}
                        </Tooltip>
                      }
                    >
                      <PlusOutlined />
                    </OverlayTrigger>
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    onShowAddQuestion(true);
                  }}
                />
              </div>
              <Divider style={{ margin: '2px 0' }} />
              {menu}
            </div>
          )}
        >
          {filteredQuestions.map((x) => (
            <Option
              key={x.testId}
              value={x.testId}
              className="d-flex border-0 p-0 w-100 cursor-normal"
            >
              <div className="d-flex align-items-center justify-content-between w-100">
                <div className="ml-2 w-75">
                  <span
                    className="text-dark font-weight-600 break-word text-truncate d-block"
                    title={x?.text}
                  >
                    {x?.text}
                  </span>
                  <span className="text-xs text-muted">
                    {t(`CREATE_SCREENING_YOUR_QUESTIONS_${x?.answerType}`)}
                  </span>
                </div>
                <div className="d-flex align-items-center">
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip className="" placement="top">
                        {t('CREATE_ADDITIONAL_QUESTIONS_ADD')}
                      </Tooltip>
                    }
                  >
                    <Button
                      type="link"
                      className="mr-1"
                      icon={<PlusOutlined />}
                      data-id={x.testId}
                      key={x.testId}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpen(false);
                        handleSelectQuestion(e.currentTarget.dataset.id);
                      }}
                    />
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip className="" placement="top">
                        {t('CREATE_ADDITIONAL_QUESTIONS_EDIT')}
                      </Tooltip>
                    }
                  >
                    <Button
                      type="link"
                      icon={
                        <EditPencilIcon className="icon" fill="#757575" width="14" height="14" />
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpen(false);

                        // Validar si la pregunta ya está agregada
                        const alreadyAdded = customTestGroup?.some((q) => q.testId === x.testId || q.id === x.testId);

                        if (alreadyAdded) {
                          notification.open({
                            message: t('CREATE_ADDITIONAL_QUESTIONS_ALREADY_ADDED'),
                            type: 'warning',
                          });
                        } else {
                          onShowAddQuestion({ show: true, questionToEdit: x });
                        }
                      }}
                    />
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip className="" placement="top">
                        {t('CREATE_ADDITIONAL_QUESTIONS_DELETE')}
                      </Tooltip>
                    }
                  >
                    <Button
                      type="link"
                      danger
                      className="mt-1 ml-1"
                      icon={<DeleteForever fill="#4987be" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpen(false);
                        setQuestionToDelete(x);
                      }}
                    />
                  </OverlayTrigger>
                </div>
              </div>
            </Option>
          ))}
        </Select>
      </div>
    </div>
  );
}
